/**
 * voiceRecorderMachine — les états de l'enregistreur vocal, en réducteur pur.
 *
 * Sept états : `idle` (rien), `requesting` (le micro est demandé), `denied` (refusé), `recording`,
 * `paused`, `recorded` (un fichier est prêt à réécouter ou à envoyer) et `error`. Le crochet
 * `useVoiceRecorder` branche les effets (le micro, `expo-audio`, la session audio, les minuteurs,
 * l'arrière-plan) et ne fait que lui envoyer des événements datés : la durée se calcule ici, sur
 * l'horloge que le crochet passe (`at`), jamais en lisant l'enregistreur chaque seconde.
 *
 * La durée tient en deux nombres : ce qui est enregistré dans les segments clos (`accumulatedMs`) et
 * le début du segment en cours (`segmentStartedAt`). Un écran en tire le temps qui court sans se
 * redessiner chaque seconde : c'est le compteur, pas l'écran, qui avance.
 *
 * Rien ici n'est propre à un rôle ni à une surface : la messagerie de l'élève, l'activité orale et
 * le vocal de bilan du tuteur en sont trois lecteurs possibles.
 */

export type VoiceRecorderStatus = "idle" | "requesting" | "denied" | "recording" | "paused" | "recorded" | "error"

/** Pourquoi l'enregistrement s'est arrêté : le geste, le plafond, ou l'app passée en arrière-plan. */
export type VoiceStopReason = "user" | "max_duration" | "background"

/**
 * Ce que l'écran doit dire, une fois : le plafond atteint (l'enregistrement est gardé), l'app passée
 * en arrière-plan (gardé aussi), ou une fin demandée sous la durée minimale (en pause).
 */
export type VoiceRecorderNotice = "max_duration" | "background" | "too_short"

export type VoiceRecorderErrorState = { kind: "start_failed" | "stop_failed"; message: string }

export type VoiceRecorderState = {
    status: VoiceRecorderStatus
    /** La durée enregistrée dans les segments clos, en ms ; une fois `recorded`, la durée du fichier. */
    accumulatedMs: number
    /** Le début du segment en cours (horloge du crochet, ms) ; `null` hors enregistrement. */
    segmentStartedAt: number | null
    /** L'arrêt est demandé, le fichier n'est pas encore écrit : plus aucun geste ne part. */
    stopping: boolean
    stopReason: VoiceStopReason | null
    /** Le fichier enregistré (`recorded`) : son adresse locale, son vrai type et son extension. */
    uri: string | null
    mimeType: string | null
    extension: string | null
    error: VoiceRecorderErrorState | null
    notice: VoiceRecorderNotice | null
    /** Refusé : le système peut-il encore demander ? Sinon, il faut passer par les réglages. */
    canAskAgain: boolean
}

export type VoiceRecorderEvent =
    | { type: "START_REQUESTED" }
    | { type: "PERMISSION_DENIED"; canAskAgain: boolean }
    | { type: "RECORDING_STARTED"; at: number }
    | { type: "START_FAILED"; message: string }
    | { type: "PAUSED"; at: number }
    | { type: "RESUMED"; at: number }
    | { type: "TOO_SHORT"; at: number }
    | { type: "STOP_REQUESTED"; at: number; reason: VoiceStopReason }
    | { type: "STOPPED"; uri: string; mimeType: string; extension: string; measuredMs?: number | null }
    | { type: "STOP_FAILED"; message: string }
    | { type: "RESET" }

/** Le plafond d'un enregistrement : 12 minutes, comme aujourd'hui. */
export const MAX_RECORDING_DURATION_MS = 12 * 60 * 1000

export const initialVoiceRecorderState: VoiceRecorderState = {
    status: "idle",
    accumulatedMs: 0,
    segmentStartedAt: null,
    stopping: false,
    stopReason: null,
    uri: null,
    mimeType: null,
    extension: null,
    error: null,
    notice: null,
    canAskAgain: true,
}

const live = (state: VoiceRecorderState) => state.status === "recording" || state.status === "paused"

/** Le segment en cours refermé à `at` : sa durée rejoint les segments clos. */
function closeSegment(state: VoiceRecorderState, at: number): number {
    if (state.segmentStartedAt == null) return state.accumulatedMs
    return state.accumulatedMs + Math.max(0, at - state.segmentStartedAt)
}

const NOTICE_OF: Record<VoiceStopReason, VoiceRecorderNotice | null> = {
    user: null,
    max_duration: "max_duration",
    background: "background",
}

export function voiceRecorderReducer(state: VoiceRecorderState, event: VoiceRecorderEvent): VoiceRecorderState {
    switch (event.type) {
        case "START_REQUESTED":
            if (state.status === "requesting" || live(state)) return state
            return { ...initialVoiceRecorderState, status: "requesting" }
        case "PERMISSION_DENIED":
            if (state.status !== "requesting") return state
            return { ...initialVoiceRecorderState, status: "denied", canAskAgain: event.canAskAgain }
        case "RECORDING_STARTED":
            if (state.status !== "requesting") return state
            return { ...initialVoiceRecorderState, status: "recording", segmentStartedAt: event.at }
        case "START_FAILED":
            if (state.status !== "requesting") return state
            return {
                ...initialVoiceRecorderState,
                status: "error",
                error: { kind: "start_failed", message: event.message },
            }
        case "PAUSED":
            if (state.status !== "recording" || state.stopping) return state
            return { ...state, status: "paused", accumulatedMs: closeSegment(state, event.at), segmentStartedAt: null }
        case "RESUMED":
            if (state.status !== "paused" || state.stopping) return state
            return { ...state, status: "recording", segmentStartedAt: event.at, notice: null }
        case "TOO_SHORT":
            if (!live(state) || state.stopping) return state
            return {
                ...state,
                status: "paused",
                accumulatedMs: closeSegment(state, event.at),
                segmentStartedAt: null,
                notice: "too_short",
            }
        case "STOP_REQUESTED":
            if (!live(state) || state.stopping) return state
            return {
                ...state,
                stopping: true,
                stopReason: event.reason,
                accumulatedMs: closeSegment(state, event.at),
                segmentStartedAt: null,
            }
        case "STOPPED": {
            if (!state.stopping) return state
            const measured = event.measuredMs != null && event.measuredMs > 0 ? event.measuredMs : null
            return {
                ...state,
                status: "recorded",
                stopping: false,
                accumulatedMs: measured ?? state.accumulatedMs,
                uri: event.uri,
                mimeType: event.mimeType,
                extension: event.extension,
                notice: NOTICE_OF[state.stopReason ?? "user"],
            }
        }
        case "STOP_FAILED":
            if (!state.stopping) return state
            return {
                ...initialVoiceRecorderState,
                status: "error",
                error: { kind: "stop_failed", message: event.message },
            }
        case "RESET":
            return initialVoiceRecorderState
        default:
            return state
    }
}

/** La durée enregistrée à l'instant `now`, segment en cours compris. */
export function elapsedMs(state: VoiceRecorderState, now: number): number {
    return closeSegment(state, now)
}

/** Ce qui reste avant le plafond, à l'instant `now`. */
export function remainingMs(state: VoiceRecorderState, now: number, maxMs: number): number {
    return Math.max(0, maxMs - elapsedMs(state, now))
}

/** Une fin demandée sous la durée minimale (`minMs` > 0) est refusée : l'enregistrement se met en pause. */
export function isBelowMinimum(state: VoiceRecorderState, now: number, minMs: number): boolean {
    return minMs > 0 && elapsedMs(state, now) < minMs
}
